/**
 * @file useMeetingMomLanguage.ts
 * @brief Language pills + MoM overlay for Meeting Intel detail (mobile parity)
 *
 * @details
 * Prefers `meeting.translations` cache (so Urdu generated on mobile appears
 * instantly on web). On miss, POST /translate (member routes). Returns a
 * display meeting with overlay MoM fields and `contentRtl` for layout.
 *
 * @author DES
 * @date 13-07-2026
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { ApiClientError } from "@/shared/api/client";
import { toast } from "@/shared/hooks/useToast";
import {
  getLanguageEnglishName,
  isRtlLanguageCode,
  languageBaseCode,
  sameLanguageBase,
} from "@/shared/utils/languages";
import { useTranslateCompanyMeetingMom } from "../api";
import type { MeetingActionsScope } from "../components/MeetingRowActions";
import type {
  MeetingDetail,
  TranslateMomResponse,
} from "../schemas";

type CachedTranslation = {
  meetingOverview?: string;
  meetingSummary?: string;
  summaryOverview?: string;
  keyDecisions?: string[];
  keyPoints?: MeetingDetail["key_points"];
  attentionRisks?: string;
  todos?: MeetingDetail["todos"];
};

function isCacheComplete(cached: CachedTranslation | null | undefined): boolean {
  if (!cached) return false;
  return (
    typeof cached.meetingOverview === "string" &&
    cached.meetingOverview.trim().length > 0 &&
    typeof cached.meetingSummary === "string" &&
    Array.isArray(cached.keyDecisions) &&
    Array.isArray(cached.keyPoints) &&
    Array.isArray(cached.todos)
  );
}

function findCachedTranslation(
  translations: MeetingDetail["translations"],
  lang: string,
): { key: string; cached: CachedTranslation } | null {
  if (!translations || typeof translations !== "object") return null;
  const want = lang.toLowerCase().trim();
  const direct = translations[want];
  if (direct && typeof direct === "object") {
    return { key: want, cached: direct as CachedTranslation };
  }
  const wantBase = languageBaseCode(want);
  for (const [key, value] of Object.entries(translations)) {
    if (
      value &&
      typeof value === "object" &&
      languageBaseCode(key) === wantBase
    ) {
      return { key, cached: value as CachedTranslation };
    }
  }
  return null;
}

function overlayFromCacheOrApi(
  data: CachedTranslation | TranslateMomResponse,
): CachedTranslation {
  return {
    meetingOverview: data.meetingOverview ?? undefined,
    meetingSummary:
      data.meetingSummary ?? data.summaryOverview ?? undefined,
    summaryOverview:
      data.summaryOverview ?? data.meetingSummary ?? undefined,
    keyDecisions: data.keyDecisions ?? undefined,
    keyPoints: data.keyPoints ?? undefined,
    attentionRisks: data.attentionRisks ?? undefined,
    todos: data.todos ?? undefined,
  };
}

function buildLanguageOptions(meeting: MeetingDetail): string[] {
  const out: string[] = [];
  const push = (raw?: string | null) => {
    if (!raw?.trim()) return;
    const code = raw.trim();
    if (out.some((x) => sameLanguageBase(x, code))) return;
    out.push(code);
  };

  const base = meeting.target_languages?.[0] || "en";
  push(base);
  for (const lang of meeting.target_languages ?? []) push(lang);
  for (const key of Object.keys(meeting.translations ?? {})) {
    // Skip non-language keys if any slip into the map.
    if (!languageBaseCode(key)) continue;
    push(key);
  }
  return out;
}

export function useMeetingMomLanguage(
  meeting: MeetingDetail,
  scope: MeetingActionsScope,
) {
  const baseLanguage = meeting.target_languages?.[0] || "en";
  const [selectedLanguage, setSelectedLanguage] = useState(baseLanguage);
  const [overlay, setOverlay] = useState<CachedTranslation | null>(null);
  const [isTranslating, setIsTranslating] = useState(false);

  // No admin translate: VERBLIZR staff never read meeting content (B2/W4,
  // DES 22-10-2026), and the Super Admin page has no MoM.
  void scope;
  const translate = useTranslateCompanyMeetingMom();

  const languageOptions = useMemo(
    () => buildLanguageOptions(meeting),
    [meeting],
  );

  useEffect(() => {
    setSelectedLanguage(meeting.target_languages?.[0] || "en");
    setOverlay(null);
  }, [meeting.id, meeting.target_languages]);

  const selectLanguage = useCallback(
    async (lang: string) => {
      if (sameLanguageBase(lang, selectedLanguage) && !isTranslating) {
        return;
      }

      if (sameLanguageBase(lang, baseLanguage)) {
        setSelectedLanguage(lang);
        setOverlay(null);
        return;
      }

      const hit = findCachedTranslation(meeting.translations, lang);
      if (hit && isCacheComplete(hit.cached)) {
        setSelectedLanguage(lang);
        setOverlay(overlayFromCacheOrApi(hit.cached));
        return;
      }

      setSelectedLanguage(lang);
      setIsTranslating(true);
      try {
        const apiLang = hit?.key || lang.toLowerCase().trim();
        const result = await translate.mutateAsync({
          id: meeting.id,
          targetLanguage: apiLang,
          scope: "all",
        });
        setOverlay(overlayFromCacheOrApi(result));
      } catch (err) {
        setSelectedLanguage(baseLanguage);
        setOverlay(null);
        const message =
          err instanceof ApiClientError
            ? err.message
            : "Could not load this language. Try again.";
        toast({ title: "Translation unavailable", description: message });
      } finally {
        setIsTranslating(false);
      }
    },
    [
      selectedLanguage,
      isTranslating,
      baseLanguage,
      meeting.translations,
      meeting.id,
      translate,
    ],
  );

  const displayMeeting = useMemo((): MeetingDetail => {
    if (!overlay) return meeting;
    return {
      ...meeting,
      meeting_overview: overlay.meetingOverview ?? meeting.meeting_overview,
      meeting_summary:
        overlay.meetingSummary ??
        overlay.summaryOverview ??
        meeting.meeting_summary,
      summary_overview:
        overlay.summaryOverview ??
        overlay.meetingSummary ??
        meeting.summary_overview,
      key_decisions: overlay.keyDecisions ?? meeting.key_decisions,
      key_points: overlay.keyPoints ?? meeting.key_points,
      attention_risks: overlay.attentionRisks ?? meeting.attention_risks,
      todos: overlay.todos ?? meeting.todos,
    };
  }, [meeting, overlay]);

  const contentRtl = useMemo(() => {
    if (!isRtlLanguageCode(selectedLanguage)) return false;
    if (sameLanguageBase(selectedLanguage, baseLanguage)) return true;
    return !!overlay && !isTranslating;
  }, [selectedLanguage, baseLanguage, overlay, isTranslating]);

  const languagePills = useMemo(
    () =>
      languageOptions.map((code) => ({
        code,
        label: getLanguageEnglishName(code, code.toUpperCase()),
        active: sameLanguageBase(code, selectedLanguage),
      })),
    [languageOptions, selectedLanguage],
  );

  return {
    displayMeeting,
    selectedLanguage,
    languagePills,
    selectLanguage,
    isTranslating,
    contentRtl,
    showLanguagePills: languagePills.length > 1,
  };
}
